/**
 * Maps Firebase Auth error codes to human-readable messages.
 *
 * Firebase throws errors with codes like `auth/user-not-found` and exposes the
 * raw `Firebase: Error (auth/user-not-found).` text on the `.message`
 * property. Surfacing that string to users is jarring — this helper turns it
 * into something people can act on.
 *
 * Usage:
 *   try {
 *     await signInWithEmailAndPassword(auth, email, password)
 *   } catch (e) {
 *     store.commit('setError', friendlyAuthError(e))
 *   }
 *
 * Coverage notes:
 * - We treat the modern `auth/invalid-credential` (Firebase's unified
 *   wrong-email / wrong-password code as of 2024+) the same way as the
 *   legacy `auth/wrong-password` and `auth/user-not-found` — a single
 *   "Email or password is incorrect" message that doesn't reveal whether
 *   the email exists.
 * - For password-reset flows we never want to leak existence either, so
 *   the caller should map success and "user-not-found" to the same notice
 *   ("If an account exists, we've sent a reset link"). This helper still
 *   returns a sensible message for that code if a caller chooses to use
 *   it directly.
 * - Some codes are SSO-popup specific (popup-closed-by-user etc.). We map
 *   those to "Sign-in was cancelled" rather than reading as a hard error
 *   because the user almost certainly intended to dismiss it.
 *
 * Reference: https://firebase.google.com/docs/auth/admin/errors
 */

type MaybeFirebaseError = {
  code?: unknown;
  message?: unknown;
  customData?: unknown;
};

const FRIENDLY_MESSAGES: Record<string, string> = {
  // ----- Email / password sign-in --------------------------------------
  // Modern unified code — covers wrong email AND wrong password without
  // revealing which one was wrong.
  'auth/invalid-credential': 'Email or password is incorrect. Please try again.',
  'auth/invalid-login-credentials': 'Email or password is incorrect. Please try again.',
  'auth/wrong-password': 'Email or password is incorrect. Please try again.',
  'auth/user-not-found': 'Email or password is incorrect. Please try again.',
  'auth/invalid-password': 'Email or password is incorrect. Please try again.',

  // ----- Account state -------------------------------------------------
  'auth/user-disabled': 'This account has been disabled. Please contact support@hiiker.com.',
  'auth/user-token-expired': 'Your session expired. Please log in again.',
  'auth/requires-recent-login': 'For security, please log in again to continue.',
  'auth/user-mismatch': 'That credential belongs to a different account.',

  // ----- Sign up --------------------------------------------------------
  'auth/email-already-in-use': 'An account with that email already exists. Try logging in instead.',
  'auth/email-already-exists': 'An account with that email already exists. Try logging in instead.',
  'auth/credential-already-in-use': 'That credential is already linked to another account.',
  'auth/account-exists-with-different-credential':
    'An account with that email already exists, but with a different sign-in method. Try the other option.',

  // ----- Validation ----------------------------------------------------
  'auth/invalid-email': 'That doesn\'t look like a valid email address.',
  'auth/missing-email': 'Please enter your email address.',
  'auth/missing-password': 'Please enter your password.',
  'auth/weak-password': 'Choose a stronger password — at least 6 characters.',

  // ----- Rate limiting / availability ----------------------------------
  'auth/too-many-requests': 'Too many attempts. Please wait a moment and try again.',
  'auth/quota-exceeded': 'We\'re a bit busy right now. Please try again in a moment.',
  'auth/network-request-failed': 'Network connection lost. Check your internet and try again.',
  'auth/timeout': 'That took too long. Please try again.',
  'auth/internal-error': 'Something went wrong on our side. Please try again.',

  // ----- SSO popups ----------------------------------------------------
  'auth/popup-closed-by-user': 'Sign-in was cancelled.',
  'auth/cancelled-popup-request': 'Sign-in was cancelled. Please try again.',
  'auth/popup-blocked': 'Your browser blocked the sign-in popup. Please allow popups for HiiKER and try again.',
  'auth/user-cancelled': 'Sign-in was cancelled.',
  'auth/redirect-cancelled-by-user': 'Sign-in was cancelled.',
  'auth/redirect-operation-pending': 'Another sign-in is already in progress.',
  'auth/web-storage-unsupported':
    'Your browser is blocking storage we need to sign you in. Try disabling private/incognito mode or third-party cookie blocking.',
  'auth/operation-not-supported-in-this-environment':
    'Sign-in isn\'t supported in this browser. Please try a different browser.',

  // ----- Domain / config ------------------------------------------------
  'auth/unauthorized-domain':
    'Sign-in isn\'t available from this address. Please contact support@hiiker.com.',
  'auth/unauthorized-continue-uri':
    'This link can\'t be used here. Please request a fresh one.',
  'auth/operation-not-allowed':
    'This sign-in method isn\'t enabled. Please contact support@hiiker.com.',
  'auth/admin-restricted-operation': 'This action isn\'t available right now.',

  // ----- Email-link / magic-link / reset --------------------------------
  'auth/expired-action-code': 'This link has expired. Please request a new one.',
  'auth/invalid-action-code': 'This link is invalid or has already been used. Please request a new one.',
  'auth/missing-action-code': 'Sign-in link is missing. Please request a new one.',
  'auth/unverified-email': 'Please verify your email address before continuing.',

  // ----- MFA / verification --------------------------------------------
  'auth/multi-factor-auth-required': 'Two-factor authentication is required for this account.',
  'auth/invalid-verification-code': 'That verification code is incorrect.',
  'auth/invalid-verification-id': 'That verification request is no longer valid. Please try again.',
  'auth/missing-verification-code': 'Please enter your verification code.',
  'auth/missing-verification-id': 'Verification request is missing. Please try again.',
  'auth/maximum-second-factor-count-exceeded':
    'You\'ve reached the maximum number of second factors for this account.',

  // ----- Misc -----------------------------------------------------------
  'auth/captcha-check-failed': 'CAPTCHA check failed. Please try again.',
  'auth/no-such-provider': 'That sign-in method isn\'t configured for this account.',
};

const DEFAULT_FALLBACK = 'Something went wrong. Please try again, or contact support@hiiker.com if the problem persists.';

/**
 * Strip Firebase's `Firebase: Error (auth/...)` wrapper from a raw message.
 * Used as a last resort when the error has no `.code` we recognise but still
 * has a Firebase-shaped message string.
 */
function extractCode(rawMessage: string): string | null {
  const match = rawMessage.match(/auth\/[a-z0-9-]+/i);
  return match ? match[0].toLowerCase() : null;
}

export function friendlyAuthError(error: unknown, fallback: string = DEFAULT_FALLBACK): string {
  if (!error) return fallback;

  const err = error as MaybeFirebaseError;

  // Preferred path: structured Firebase error with a `.code`.
  if (typeof err.code === 'string' && FRIENDLY_MESSAGES[err.code]) {
    return FRIENDLY_MESSAGES[err.code];
  }

  // Fallback: try to recover the code from a raw "Firebase: Error (auth/...)" string.
  if (typeof err.message === 'string') {
    const recovered = extractCode(err.message);
    if (recovered && FRIENDLY_MESSAGES[recovered]) {
      return FRIENDLY_MESSAGES[recovered];
    }
  }

  // Last resort — known unrecognised Firebase error shape: hide the raw
  // `Firebase: Error (...)` wrapper and surface our generic fallback.
  if (typeof err.message === 'string' && /^Firebase:/i.test(err.message)) {
    return fallback;
  }

  // Plain-string errors that don't look like Firebase output can pass through
  // unmodified — they're likely intentional human-readable messages from
  // upstream code.
  if (typeof error === 'string') return error;
  if (typeof err.message === 'string' && err.message.length > 0) return err.message;

  return fallback;
}
